<template>
  <div class="bg-white rounded-xl shadow-sm p-6 min-h-[calc(100vh-2rem)]">
    <!-- Header -->
    <div class="flex items-center space-x-4 mb-6">
      <h2 class="text-xl font-bold text-gray-800">用户管理</h2>
      
      <div class="flex items-center space-x-2 flex-1">
        <div class="relative">
          <input 
            v-model="searchQuery"
            @input="handleSearch"
            type="text" 
            placeholder="检索：姓名" 
            class="pl-3 pr-10 py-1.5 border border-gray-200 rounded-md text-sm w-64 focus:outline-none focus:border-green-500 text-gray-600 placeholder-gray-400"
          >
        </div>

        <button 
          @click="openCreateModal"
          class="bg-green-800 text-white px-4 py-1.5 rounded-md text-sm hover:bg-green-900 transition flex items-center font-medium"
        >
          新建用户
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full text-left text-sm text-gray-600 table-auto">
        <thead class="bg-gray-50 text-gray-500 font-medium">
          <tr>
            <th class="py-3 px-6 ">用户ID</th>
            <th class="py-3 px-6 ">姓名</th>
            <th class="py-3 px-6 ">性别</th>
            <th class="py-3 px-6 ">年龄</th>
            <th class="py-3 px-6 ">更新时间</th>
            <th class="py-3 px-6 text-right">操作</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-if="loading" class="bg-white">
             <td colspan="8" class="py-10 text-center text-gray-400">加载中...</td>
          </tr>
          <tr v-else-if="patients.length === 0" class="bg-white">
             <td colspan="8" class="py-10 text-center text-gray-400">暂无数据</td>
          </tr>
          <tr 
            v-for="patient in patients" 
            :key="patient.id"
            class="hover:bg-gray-50 transition group"
          >
            <td class="py-4 px-6">{{ patient.id }}</td>
            <td class="py-4 px-6 font-medium text-gray-800">{{ patient.name }}</td>
            <td class="py-4 px-6">{{ patient.gender }}</td>
            <td class="py-4 px-6">{{ patient.age }}</td>
            <td class="py-4 px-6 text-gray-500">{{ patient.updatedAt }}</td>
            <td class="py-4 px-6 flex justify-end space-x-2">
              <button @click="editPatient(patient)" class="bg-primary-green text-white px-3 py-1 rounded text-xs hover:bg-green-600 transition">编辑</button>
              <button @click="goToDiagnosisRecords(patient)" class="bg-primary-green text-white px-3 py-1 rounded text-xs hover:bg-green-600 transition">问诊记录</button>
              <button @click="confirmDelete(patient)" class="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 transition">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex justify-between items-center mt-6 text-sm text-gray-500">
      <div class="flex items-center space-x-2">
        <span>第 1/1 页 共 {{ patients.length }} 条</span>
        <select class="border border-gray-200 rounded px-2 py-1 focus:outline-none bg-white">
          <option>10/页</option>
        </select>
      </div>
      <div class="flex space-x-2">
        <button class="px-3 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-600" disabled>上一页</button>
        <button class="px-3 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-600" disabled>下一页</button>
      </div>
    </div>

    <!-- Create/Edit Patient Modal -->
    <div v-if="showCreatePatientModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 transform transition-all scale-100">
        <h3 class="text-xl font-bold text-gray-800 mb-6">{{ isEditing ? '编辑用户' : '新增用户' }}</h3>
        
        <form @submit.prevent="handleSavePatient" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">姓名 <span class="text-red-500">*</span></label>
            <input v-model="formPatient.name" type="text" required class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500">
          </div>
          
          <div class="grid grid-cols-2 gap-4">
             <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">出生日期 <span class="text-red-500">*</span></label>
              <input v-model="formPatient.birthday" type="date" required class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">性别 <span class="text-red-500">*</span></label>
              <select v-model="formPatient.gender" required class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 bg-white">
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">备注</label>
            <textarea v-model="formPatient.remark" rows="3" class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"></textarea>
          </div>

          <div class="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
            <button type="button" @click="closeModal" class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition">取消</button>
            <button type="submit" :disabled="saving" class="px-6 py-2 bg-primary-green text-white rounded-lg hover:bg-green-600 transition shadow-sm disabled:opacity-70">
              {{ saving ? '保存中...' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 transform transition-all scale-100">
        <h3 class="text-xl font-bold text-gray-800 mb-4">确认删除</h3>
        <p class="text-gray-600 mb-6">确定要删除用户 <span class="font-bold text-gray-800">{{ patientToDelete?.name }}</span> 吗？此操作无法撤销。</p>
        
        <div class="flex justify-end space-x-3">
          <button @click="showDeleteConfirm = false" class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition">取消</button>
          <button @click="handleDelete" :disabled="deleting" class="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition shadow-sm disabled:opacity-70">
            {{ deleting ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { httpRequest } from '@/utils/request';

export default {
  name: 'PatientManagement',
  data() {
    return {
      patients: [],
      searchQuery: '',
      loading: false,
      showCreatePatientModal: false,
      formPatient: {
        id: null,
        name: '',
        birthday: '',
        mobile: '',
        gender: '男',
        remark: ''
      },
      saving: false,
      
      // Delete confirmation
      showDeleteConfirm: false,
      patientToDelete: null,
      deleting: false
    }
  },
  computed: {
    isEditing() {
      return !!this.formPatient.id;
    }
  },
  mounted() {
    this.fetchPatients();
  },
  methods: {
    async fetchPatients() {
      this.loading = true;
      try {
        const response = await httpRequest('get', '/v1/patients', {
          headers: {
            'Client-Type': '1'
          },
          query: {
            name: this.searchQuery || ''
          }
        });
        const payload_data = response && response.data ? response.data : response;
        const list_source = payload_data && Array.isArray(payload_data.data) ? payload_data.data : [];
        this.patients = list_source.map((item) => {
          const gender_value = item.gender;
          let gender_text = '';
          if (gender_value === 1 || gender_value === '1') {
            gender_text = '男';
          } else if (gender_value === 2 || gender_value === '2') {
            gender_text = '女';
          } else {
            gender_text = gender_value || '';
          }
          let updated_at = '';
          if (item.updated_at) {
            updated_at = item.updated_at;
          } else if (item.created_at) {
            updated_at = item.created_at;
          }
          return {
            id: item.id,
            name: item.name,
            gender: gender_text,
            age: item.age,
            birthday: item.birthday || '',
            remark: item.remark || '',
            updatedAt: updated_at
          };
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      // Simple debounce
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchPatients();
      }, 300);
    },
    openCreateModal() {
      this.formPatient = { id: null, name: '', birthday: '', mobile: '', gender: '男', remark: '' };
      this.showCreatePatientModal = true;
    },
    editPatient(patient) {
      this.formPatient = { ...patient };
      this.showCreatePatientModal = true;
    },
    closeModal() {
      this.showCreatePatientModal = false;
      this.formPatient = { id: null, name: '', birthday: '', mobile: '', gender: '男', remark: '' };
    },
    async handleSavePatient() {
      this.saving = true;
      try {
        const gender_value = this.formPatient.gender === '女' ? 2 : 1;
        const request_body = {
          name: this.formPatient.name,
          mobile: this.formPatient.mobile || '',
          birthday: this.formPatient.birthday,
          gender: gender_value,
          remark: this.formPatient.remark || ''
        };
        if (this.isEditing) {
          await httpRequest('put', `/v1/patients/${this.formPatient.id}`, {
            headers: {
              'Client-Type': '1'
            },
            body: request_body
          });
        } else {
          await httpRequest('post', '/v1/patients', {
            headers: {
              'Client-Type': '1'
            },
            body: request_body
          });
        }
        this.closeModal();
        this.fetchPatients(); // Refresh list
      } catch (error) {
        alert(this.isEditing ? '更新失败' : '创建失败');
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(patient) {
      this.patientToDelete = patient;
      this.showDeleteConfirm = true;
    },
    async handleDelete() {
      if (!this.patientToDelete) return;
      this.deleting = true;
      try {
        await httpRequest('delete', `/v1/patients/${this.patientToDelete.id}`, {
          headers: {
            'Client-Type': '1'
          }
        });
        this.showDeleteConfirm = false;
        this.patientToDelete = null;
        this.fetchPatients();
      } catch (error) {
        alert('删除失败');
      } finally {
        this.deleting = false;
      }
    },
    goToDiagnosisRecords(patient) {
      if (!patient || !patient.id) return;
      this.$router.push({
        name: 'DiagnosisRecords',
        query: {
          patient_id: patient.id
        }
      });
    }
  }
}
</script>
